import React, { useState, useEffect, useCallback } from "react";
import { Button, Input, Modal, Text, Loading } from '@nextui-org/react';
import { useSelector } from "react-redux";
import { Delete, Edit } from "react-iconly";
import cloneDeep from 'lodash/cloneDeep';

import { secureFetch } from "../../lib/api";
import { success, error } from "../../lib/userAlerts";
import { mstore } from "../../store";
import endpoint from "../../config/endpoint.json";

const FIELD_TYPES = {
  TEXT: ['usr_text1_lbl', 'usr_text2_lbl', 'usr_text3_lbl'],
  NUMBER: ['usr_num1_lbl', 'usr_num2_lbl', 'usr_num3_lbl'],
  DATE: ['usr_date1_lbl', 'usr_date2_lbl', 'usr_date3_lbl']
};

const StatusBadge = ({ hasLabel }) => (
  <div className="w-max">
    {!hasLabel ? (
      <div className="relative grid items-center font-sans font-bold uppercase whitespace-nowrap select-none bg-red-500/20 text-red-700 py-1 px-2 text-xs rounded-md">
        <span>Not Assigned</span>
      </div>
    ) : (
      <div className="relative grid items-center font-sans font-bold uppercase whitespace-nowrap select-none bg-green-500/20 text-green-600 py-1 px-2 text-xs rounded-md">
        <span>Name Assigned</span>
      </div>
    )}
  </div>
);

const CustomFieldRow = ({ field, onEdit, onDelete }) => (
  <tr>
    <td className="p-2 border-b border-blue-gray-50">
      <div className="flex items-center gap-3">
        <p className="block antialiased text-md leading-normal font-normal">{field.dataType}</p>
      </div>
    </td>
    <td className="p-2 border-b border-blue-gray-50">
      <div className="flex items-center gap-3">
        <p className="block antialiased font-semibold text-md leading-normal text-gray-600 font-normal">
          {field.fieldLable}
        </p>
      </div>
    </td>
    <td className="p-2 border-b border-blue-gray-50">
      <StatusBadge hasLabel={field.fieldLable} />
    </td>
    <td className="p-2 border-b border-blue-gray-50">
      <button 
        className="text-primary relative align-middle select-none font-sans font-medium text-center uppercase transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none w-10 max-w-[40px] h-10 max-h-[40px] rounded-lg text-xs" 
        type="button"
        onClick={() => onEdit(field)}
      >
        <Edit />
      </button>
      <button 
        disabled={!field.fieldLable} 
        className="relative align-middle select-none font-sans font-medium text-center uppercase transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none w-10 max-w-[40px] h-10 max-h-[40px] rounded-lg text-xs text-blue-gray-500 hover:bg-blue-gray-500/10 active:bg-blue-gray-500/30" 
        type="button"
        onClick={() => onDelete(field)}
      >
        <Delete primaryColor="red" />
      </button>
    </td>
  </tr>
);

const CustomFieldsTable = ({ fields, onEdit, onDelete }) => (
  <div className="overflow-scroll w-full border-2 border-dotted my-4 p-4 ml2 shadow-lg rounded-xl">
    <div className="flex justify-between">
      <div className="f4">Shipment Data - Custom Fields</div>
    </div>
    <table className="mt-4 w-full min-w-max table-auto text-left">
      <thead>
        <tr>
          {['Field Type', 'Field Label', 'Status', 'Actions'].map(header => (
            <th key={header} className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 px-2 py-4 transition-colors hover:bg-blue-gray-50">
              <p className="antialiased text-blue-gray-900 flex items-center justify-between gap-2 font-bold leading-none opacity-70">
                {header}
              </p>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {fields.map((field, idx) => (
          <CustomFieldRow 
            key={field.id} 
            field={field} 
            onEdit={onEdit} 
            onDelete={onDelete} 
          />
        ))}
      </tbody>
    </table>
  </div>
);

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, loading }) => (
  <Modal closeButton open={isOpen} onClose={onClose} blur css={{br: 0}}>
    <Modal.Header>
      <Text size={20}>Confirmation</Text>
    </Modal.Header>
    <Modal.Body>
      <Text size={16}>This action will remove the field label. Continue?</Text>
    </Modal.Body>
    <Modal.Footer>
      <div className="flex justify-end">
        <Button size='sm' auto flat css={{br:0}} onClick={onClose}>
          No
        </Button>
        <Button size='sm' auto color='error' css={{br:0, ml: 10}} onClick={onConfirm}>
          {loading ? <Loading type='points' color='white'/> : <Text color='currentColor'>Yes</Text>}
        </Button>
      </div>
    </Modal.Footer>
  </Modal>
);

const EditFieldModal = ({ isOpen, onClose, onSave, field, onChange, loading }) => (
  <Modal closeButton open={isOpen} onClose={onClose} blur css={{br: 0}}>
    <Modal.Header>
      <Text size={18}>Update Field Name</Text>
    </Modal.Header>
    <Modal.Body>
      <div role="alert" className="flex my-4">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-info shrink-0 w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span className="mx-2">Assign a unique name to the custom field.</span>
      </div>
      <Input
        clearable
        fullWidth
        className="border-2"
        css={{ $$inputBorderRadius: "0", w: '100%', h: 35 }}
        value={field?.fieldLable || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </Modal.Body>
    <Modal.Footer>
      <div className="flex justify-end">
        <Button flat size='sm' color='error' auto css={{br:0}} onClick={onClose}>
          Cancel
        </Button>
        <Button size='sm' auto color='primary' css={{br:0, ml: 10}} onClick={onSave}>
          {loading ? <Loading type='points' color='white'/> : <Text color='currentColor'>Update</Text>}
        </Button>
      </div>
    </Modal.Footer>
  </Modal>
);

const ShipmentCustomFields = () => {
  const clientId = useSelector(state => 
    state.client?.id || mstore.getPath(["user", "company_id"])
  );

  const [state, setState] = useState({
    customFields: [],
    rawCustomFields: [],
    loading: false,
    fetching: true,
    isDeleteModalOpen: false,
    isEditModalOpen: false,
    selectedField: null
  });

  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const getFieldType = useCallback((fieldName) => {
    if (FIELD_TYPES.TEXT.includes(fieldName)) return "Text";
    if (FIELD_TYPES.NUMBER.includes(fieldName)) return "Number";
    if (FIELD_TYPES.DATE.includes(fieldName)) return "Date";
    return "";
  }, []);

  const getCustomFields = useCallback(async () => {
    try {
      const [ok, , json] = await secureFetch(endpoint.getCustomFields, {});
      if (ok) {
        const customFields = Object.entries(json.customFields[0]).map(([field, fieldLable]) => ({
          id: Math.random(),
          field,
          fieldLable,
          dataType: getFieldType(field)
        }));

        updateState({
          customFields,
          rawCustomFields: cloneDeep(customFields),
          fetching: false
        });
      } else {
        error("Oops! error occurred during fetching custom fields!");
      }
    } catch (err) {
      error("Failed to fetch custom fields");
      updateState({ fetching: false });
    }
  }, [getFieldType]);

  const handleSaveField = useCallback(async () => {
    updateState({ loading: true });
    try {
      const [ok, , json] = await secureFetch(endpoint.updateCustomFields, {
        column: state.selectedField.field,
        value: state.selectedField.fieldLable,
      });

      if (ok) {
        await getCustomFields();
        success(json.message);
      } else {
        error("Oops! error occurred during save shipment fields!");
      }
    } catch (err) {
      error("Failed to save field");
    } finally {
      updateState({ 
        loading: false, 
        isDeleteModalOpen: false, 
        isEditModalOpen: false,
        selectedField: null 
      });
    }
  }, [state.selectedField, getCustomFields]);

  const handleEdit = useCallback((field) => {
    updateState({
      selectedField: field,
      isEditModalOpen: true
    });
  }, []);

  const handleDelete = useCallback((field) => {
    updateState({
      selectedField: { ...field, fieldLable: null },
      isDeleteModalOpen: true
    });
  }, []);

  const handleFieldChange = useCallback((value) => {
    updateState({
      selectedField: { ...state.selectedField, fieldLable: value }
    });
  }, [state.selectedField]);

  const handleCloseModals = useCallback(() => {
    updateState({
      isDeleteModalOpen: false,
      isEditModalOpen: false,
      selectedField: null,
      loading: false
    });
  }, []);

  useEffect(() => {
    getCustomFields();
  }, [clientId, getCustomFields]);

  if (state.fetching) {
    return (
      <div className="flex justify-center mt6">
        <Loading size="md">Loading custom fields..</Loading>
      </div>
    );
  }

  return (
    <div className="mt2">
      <div className="flex flex-row">
        {state.customFields?.length > 0 && (
          <>
            <CustomFieldsTable 
              fields={state.customFields}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
            
            <DeleteConfirmationModal 
              isOpen={state.isDeleteModalOpen}
              onClose={handleCloseModals}
              onConfirm={handleSaveField}
              loading={state.loading}
            />

            <EditFieldModal 
              isOpen={state.isEditModalOpen}
              onClose={handleCloseModals}
              onSave={handleSaveField}
              field={state.selectedField}
              onChange={handleFieldChange}
              loading={state.loading}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ShipmentCustomFields;
